<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCarBookingStore } from 'dashboard/stores/carBooking';
import { useStationSchedule } from 'dashboard/components/carBooking/useStationSchedule';
import { todayStr } from 'dashboard/components/carBooking/dateTime';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import DatePicker from 'dashboard/components/carBooking/DatePicker.vue';
import TimePicker from 'dashboard/components/carBooking/TimePicker.vue';

const props = defineProps({
  conversationId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const carBooking = useCarBookingStore();

const session = computed(() => carBooking.sessionFor(props.conversationId));
const form = computed(() => session.value.form);
const offers = computed(() => session.value.offers || []);
const uiFlags = computed(() => carBooking.uiFlags);

const { pickupAvailability, dropoffAvailability } = useStationSchedule(
  props.conversationId
);

const update = patch => carBooking.setForm(props.conversationId, patch);

// Re-runs the search with the current form and stays on the offers step (the
// store keeps step === 'offers' after a successful search). The gateway is
// only hit here — never on individual date/time edits.
const updateResults = async () => {
  try {
    await carBooking.searchFromForm(props.conversationId);
  } catch (error) {
    const data = error.response?.data;
    if (data?.code === 'provider.rate_plan_ambiguous') {
      useAlert(
        t('CONVERSATION.CAR_BOOKING.PLAN.AMBIGUOUS', {
          slugs: (data.extensions?.available || []).join(', '),
        })
      );
      return;
    }
    useAlert(
      data?.detail || t('CONVERSATION.CAR_BOOKING.TRIP.NO_AVAILABILITY')
    );
  }
};

const editToTrip = () => carBooking.setStep(props.conversationId, 'trip');

const searchErrors = computed(() => session.value.searchErrors || []);

// ── rate plans (changeable here, no trip round-trip) ─────────────────────────
const selectedProviders = computed(() => session.value.providers || []);
const planByProvider = computed(() => session.value.planByProvider || {});
const planOptionsFor = provider =>
  carBooking
    .plansForProvider(props.conversationId, provider)
    .map(plan => ({ value: plan.slug, label: plan.label }));

// Changing a plan makes the current results wrong for that provider, so re-run the
// search immediately rather than leaving the list showing the previous rate.
const changePlan = async (provider, slug) => {
  if (planByProvider.value[provider] === slug) return;
  carBooking.setPlanForProvider(props.conversationId, provider, slug);
  await updateResults();
};

// ── compare tray ─────────────────────────────────────────────────────────────
const compareCount = computed(() => (session.value.compare || []).length);
const isCompared = offer => carBooking.isCompared(props.conversationId, offer);
const toggleCompare = offer =>
  carBooking.toggleCompare(props.conversationId, offer);
const openCompare = () => carBooking.setStep(props.conversationId, 'compare');

const selected = ref([]);

// ── filters ──────────────────────────────────────────────────────────────────
const filterProvider = ref('');
const filterTransmission = ref('');
const filterFuel = ref('');
const filterCategory = ref('');
const filterSeats = ref('');
const filterBags = ref('');
const acOnly = ref(false);
const instantOnly = ref(false);
const unlimitedOnly = ref(false);
const sortDir = ref('asc');

const unique = list =>
  [...new Set(list.filter(v => v != null && v !== ''))].sort();

const providers = computed(() => unique(offers.value.map(o => o.provider)));
const transmissions = computed(() =>
  unique(offers.value.map(o => o.vehicle?.transmission))
);
const fuels = computed(() => unique(offers.value.map(o => o.vehicle?.fuel)));

// Distinct seat/bag counts present, ascending — each becomes a "N+ min" option.
const uniqueCounts = key =>
  [...new Set(offers.value.map(o => o.vehicle?.[key]).filter(v => v != null))]
    .map(Number)
    .filter(n => !Number.isNaN(n))
    .sort((a, b) => a - b);

const seatCounts = computed(() => uniqueCounts('seats'));
const bagCounts = computed(() => uniqueCounts('bags'));

const seatOptions = computed(() => [
  { value: '', label: t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_SEATS_ANY') },
  ...seatCounts.value.map(n => ({
    value: String(n),
    label: t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_SEATS_MIN', { count: n }),
  })),
]);
const bagOptions = computed(() => [
  { value: '', label: t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_BAGS_ANY') },
  ...bagCounts.value.map(n => ({
    value: String(n),
    label: t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_BAGS_MIN', { count: n }),
  })),
]);

const titleCase = value =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

const withAny = (values, anyLabel) => [
  { value: '', label: anyLabel },
  ...values.map(v => ({ value: v, label: titleCase(v) })),
];

const providerOptions = computed(() =>
  withAny(
    providers.value,
    t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_PROVIDER_ANY')
  )
);
const transmissionOptions = computed(() =>
  withAny(
    transmissions.value,
    t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_TRANSMISSION_ANY')
  )
);
const fuelOptions = computed(() =>
  withAny(fuels.value, t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_FUEL_ANY'))
);

const hasActiveFilters = computed(
  () =>
    !!filterProvider.value ||
    !!filterTransmission.value ||
    !!filterFuel.value ||
    !!filterCategory.value ||
    !!filterSeats.value ||
    !!filterBags.value ||
    acOnly.value ||
    instantOnly.value ||
    unlimitedOnly.value
);

const filteredOffers = computed(() => {
  const list = offers.value.filter(o => {
    if (filterProvider.value && o.provider !== filterProvider.value)
      return false;
    if (filterCategory.value && o.category !== filterCategory.value)
      return false;
    if (
      filterTransmission.value &&
      (o.vehicle?.transmission || '').toUpperCase() !==
        filterTransmission.value.toUpperCase()
    )
      return false;
    if (
      filterFuel.value &&
      (o.vehicle?.fuel || '').toUpperCase() !== filterFuel.value.toUpperCase()
    )
      return false;
    if (
      filterSeats.value &&
      (o.vehicle?.seats || 0) < Number(filterSeats.value)
    )
      return false;
    if (filterBags.value && (o.vehicle?.bags || 0) < Number(filterBags.value))
      return false;
    if (acOnly.value && o.vehicle?.airCon !== true) return false;
    if (instantOnly.value && o.status !== 'FREE_SELL') return false;
    if (unlimitedOnly.value && o.unlimitedKm !== true) return false;
    return true;
  });
  return [...list].sort((a, b) =>
    sortDir.value === 'asc' ? a.amount - b.amount : b.amount - a.amount
  );
});

const clearFilters = () => {
  filterProvider.value = '';
  filterTransmission.value = '';
  filterFuel.value = '';
  filterCategory.value = '';
  filterSeats.value = '';
  filterBags.value = '';
  acOnly.value = false;
  instantOnly.value = false;
  unlimitedOnly.value = false;
};

// ── category carousel ─────────────────────────────────────────────────────────
// One card per distinct category, using the first offer in that category as a
// representative for image/seats and the minimum price for the "from" label.
const categories = computed(() => {
  const map = new Map();
  offers.value.forEach(offer => {
    const key = offer.category;
    if (!key) return;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        category: key,
        imageUrl: offer.vehicle?.imageUrl || null,
        seats: offer.vehicle?.seats || null,
        minAmount: offer.amount,
        currency: offer.currency,
      });
    } else if (offer.amount < existing.minAmount) {
      existing.minAmount = offer.amount;
    }
  });
  return [...map.values()];
});

const toggleCategory = category => {
  filterCategory.value = filterCategory.value === category ? '' : category;
};

// ── per-day pricing ───────────────────────────────────────────────────────────
// Rental days are derived from the trip form's pickup/dropoff dates (local
// "YYYY-MM-DD" strings). Falls back to 0 (per-day omitted) when either is
// missing rather than guessing a duration.
const rentalDays = computed(() => {
  const { pickupDate, dropoffDate } = session.value.form || {};
  if (!pickupDate || !dropoffDate) return 0;
  const start = new Date(`${pickupDate}T00:00:00`);
  const end = new Date(`${dropoffDate}T00:00:00`);
  const diff = Math.round((end - start) / 86400000);
  return diff > 0 ? diff : 1;
});

const perDayLabel = offer => {
  if (!rentalDays.value || offer.amount == null) return null;
  const amount = Math.round(offer.amount / rentalDays.value);
  return t('CONVERSATION.CAR_BOOKING.OFFERS.PER_DAY', {
    amount: `${amount} ${offer.currency}`,
  });
};

const rentingEquiv = offer => {
  const { rentingAmount, rentingCurrency, currency } = offer;
  if (rentingAmount == null || !rentingCurrency || rentingCurrency === currency)
    return null;
  return `≈ ${rentingAmount} ${rentingCurrency}`;
};

// ── selection ─────────────────────────────────────────────────────────────────
const isSelected = token => selected.value.includes(token);

const toggle = token => {
  if (isSelected(token)) {
    selected.value = selected.value.filter(item => item !== token);
  } else if (selected.value.length < 3) {
    selected.value = [...selected.value, token];
  }
};

const shareLabel = computed(() =>
  selected.value.length === 1
    ? t('CONVERSATION.CAR_BOOKING.OFFERS.SHARE_ONE')
    : t('CONVERSATION.CAR_BOOKING.OFFERS.SHARE', {
        count: selected.value.length,
      })
);

const share = () => carBooking.share(props.conversationId, selected.value);

const back = () => carBooking.setStep(props.conversationId, 'trip');
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- rate plan per provider, changeable without going back to the trip step -->
    <div
      v-if="selectedProviders.length"
      class="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-n-weak px-4 py-2"
    >
      <div
        v-for="provider in selectedProviders"
        :key="provider"
        class="flex items-center gap-2"
      >
        <span class="text-xs font-semibold capitalize text-n-slate-11">
          {{ provider }}
        </span>
        <Select
          :model-value="planByProvider[provider]"
          :options="planOptionsFor(provider)"
          :disabled="uiFlags.searching"
          class="!w-44"
          @update:model-value="changePlan(provider, $event)"
        />
      </div>
    </div>

    <!-- a leg that failed is reported here; the other legs' offers still show -->
    <div
      v-if="searchErrors.length"
      class="border-b border-n-weak bg-n-ruby-2 px-4 py-2"
    >
      <p
        v-for="error in searchErrors"
        :key="`${error.provider}-${error.ratePlan}`"
        class="m-0 text-xs text-n-ruby-11"
      >
        <span class="font-semibold capitalize">{{ error.provider }}</span>
        <span v-if="error.ratePlan"> · {{ error.ratePlan }}</span>
        — {{ error.detail || error.code }}
      </p>
    </div>

    <!-- search summary / edit bar -->
    <div
      v-if="offers.length"
      class="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-n-weak px-4 py-2.5"
    >
      <!-- pickup location (read-only) + change -->
      <div class="flex items-center gap-1.5">
        <span class="i-lucide-map-pin shrink-0 text-base text-n-slate-10" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ form.pickupLocationName }}
        </span>
        <NextButton
          link
          slate
          xs
          :label="t('CONVERSATION.CAR_BOOKING.OFFERS.EDIT_CHANGE')"
          @click="editToTrip"
        />
      </div>

      <!-- rental dates -->
      <div class="flex items-center gap-1.5 whitespace-nowrap">
        <DatePicker
          compact
          :model-value="form.pickupDate"
          :min="todayStr()"
          trigger-class="px-0 text-sm font-semibold"
          @update:model-value="update({ pickupDate: $event })"
        />
        <span class="i-lucide-arrow-right shrink-0 text-sm text-n-slate-9" />
        <DatePicker
          compact
          :model-value="form.dropoffDate"
          :min="form.pickupDate || todayStr()"
          trigger-class="px-0 text-sm font-semibold"
          @update:model-value="update({ dropoffDate: $event })"
        />
      </div>

      <!-- pickup / dropoff time -->
      <div class="flex items-center gap-3">
        <div class="flex items-center gap-1.5">
          <span
            class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('CONVERSATION.CAR_BOOKING.TRIP.PICKUP_TIME') }}
          </span>
          <TimePicker
            :model-value="form.pickupTime"
            :min-time="pickupAvailability.minTime"
            :max-time="pickupAvailability.maxTime"
            :allow-outside="pickupAvailability.allowOutside"
            :closed="pickupAvailability.closed"
            trigger-class="px-0 text-sm font-semibold"
            @update:model-value="update({ pickupTime: $event })"
          />
        </div>
        <div class="flex items-center gap-1.5">
          <span
            class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('CONVERSATION.CAR_BOOKING.TRIP.DROPOFF_TIME') }}
          </span>
          <TimePicker
            :model-value="form.dropoffTime"
            :min-time="dropoffAvailability.minTime"
            :max-time="dropoffAvailability.maxTime"
            :allow-outside="dropoffAvailability.allowOutside"
            :closed="dropoffAvailability.closed"
            trigger-class="px-0 text-sm font-semibold"
            @update:model-value="update({ dropoffTime: $event })"
          />
        </div>
      </div>

      <NextButton
        solid
        blue
        sm
        class="ml-auto"
        :label="t('CONVERSATION.CAR_BOOKING.OFFERS.UPDATE_RESULTS')"
        :disabled="uiFlags.searching"
        :is-loading="uiFlags.searching"
        @click="updateResults"
      />
    </div>

    <!-- no offers at all -->
    <div v-if="!offers.length" class="flex-1 overflow-y-auto p-4">
      <div
        class="rounded-lg border border-dashed border-n-weak px-4 py-6 text-center text-sm text-n-slate-11"
      >
        {{ t('CONVERSATION.CAR_BOOKING.OFFERS.EMPTY') }}
      </div>
    </div>

    <div v-else class="flex min-h-0 flex-1">
      <!-- filter rail -->
      <aside class="w-56 shrink-0 overflow-y-auto border-r border-n-weak p-4">
        <div v-if="providers.length > 1" class="mb-5">
          <h3
            class="mb-2.5 border-b border-n-weak pb-1.5 text-xs font-semibold text-n-slate-12"
          >
            {{ t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_PROVIDER') }}
          </h3>
          <Select
            v-model="filterProvider"
            :options="providerOptions"
            class="!w-full"
          />
        </div>

        <div v-if="transmissions.length > 1" class="mb-5">
          <h3
            class="mb-2.5 border-b border-n-weak pb-1.5 text-xs font-semibold text-n-slate-12"
          >
            {{ t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_TRANSMISSION') }}
          </h3>
          <Select
            v-model="filterTransmission"
            :options="transmissionOptions"
            class="!w-full"
          />
        </div>

        <div v-if="fuels.length > 1" class="mb-5">
          <h3
            class="mb-2.5 border-b border-n-weak pb-1.5 text-xs font-semibold text-n-slate-12"
          >
            {{ t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_FUEL') }}
          </h3>
          <Select v-model="filterFuel" :options="fuelOptions" class="!w-full" />
        </div>

        <div v-if="seatCounts.length > 1" class="mb-5">
          <h3
            class="mb-2.5 border-b border-n-weak pb-1.5 text-xs font-semibold text-n-slate-12"
          >
            {{ t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_SEATS') }}
          </h3>
          <Select
            v-model="filterSeats"
            :options="seatOptions"
            class="!w-full"
          />
        </div>

        <div v-if="bagCounts.length > 1" class="mb-5">
          <h3
            class="mb-2.5 border-b border-n-weak pb-1.5 text-xs font-semibold text-n-slate-12"
          >
            {{ t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_BAGS') }}
          </h3>
          <Select v-model="filterBags" :options="bagOptions" class="!w-full" />
        </div>

        <div class="mb-5">
          <h3
            class="mb-2.5 border-b border-n-weak pb-1.5 text-xs font-semibold text-n-slate-12"
          >
            {{ t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_MILEAGE') }}
          </h3>
          <button
            type="button"
            class="inline-flex w-full items-center gap-1.5 rounded-lg px-3 py-2 text-sm outline outline-1 -outline-offset-1 transition-colors"
            :class="
              unlimitedOnly
                ? 'bg-n-brand/10 text-n-brand outline-n-brand'
                : 'bg-n-surface-1 text-n-slate-12 outline-n-weak hover:outline-n-slate-6'
            "
            @click="unlimitedOnly = !unlimitedOnly"
          >
            <span
              class="size-3.5"
              :class="unlimitedOnly ? 'i-lucide-check' : 'i-lucide-infinity'"
            />
            {{ t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_UNLIMITED_KM') }}
          </button>
        </div>

        <div class="mb-5 flex flex-col gap-2">
          <button
            type="button"
            class="inline-flex w-full items-center gap-1.5 rounded-lg px-3 py-2 text-sm outline outline-1 -outline-offset-1 transition-colors"
            :class="
              acOnly
                ? 'bg-n-brand/10 text-n-brand outline-n-brand'
                : 'bg-n-surface-1 text-n-slate-12 outline-n-weak hover:outline-n-slate-6'
            "
            @click="acOnly = !acOnly"
          >
            <span
              class="size-3.5"
              :class="acOnly ? 'i-lucide-check' : 'i-lucide-snowflake'"
            />
            {{ t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_AC_ONLY') }}
          </button>

          <button
            type="button"
            class="inline-flex w-full items-center gap-1.5 rounded-lg px-3 py-2 text-sm outline outline-1 -outline-offset-1 transition-colors"
            :class="
              instantOnly
                ? 'bg-n-brand/10 text-n-brand outline-n-brand'
                : 'bg-n-surface-1 text-n-slate-12 outline-n-weak hover:outline-n-slate-6'
            "
            @click="instantOnly = !instantOnly"
          >
            <span
              class="size-3.5"
              :class="instantOnly ? 'i-lucide-check' : 'i-lucide-zap'"
            />
            {{ t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_INSTANT_ONLY') }}
          </button>
        </div>

        <NextButton
          v-if="hasActiveFilters"
          link
          slate
          sm
          :label="t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_CLEAR')"
          @click="clearFilters"
        />
      </aside>

      <!-- results -->
      <section class="flex-1 overflow-y-auto p-4">
        <p class="m-0 mb-3.5 text-sm text-n-slate-11">
          {{
            t('CONVERSATION.CAR_BOOKING.OFFERS.RESULTS_COUNT', {
              count: offers.length,
            })
          }}
        </p>

        <!-- category carousel -->
        <div class="mb-3.5 flex gap-2 overflow-x-auto pb-1">
          <button
            v-for="cat in categories"
            :key="cat.category"
            type="button"
            class="w-32 shrink-0 cursor-pointer rounded-xl border bg-n-surface-1 p-2.5 text-center transition-colors"
            :class="
              filterCategory === cat.category
                ? 'border-n-brand shadow-[0_0_0_1px] shadow-n-brand'
                : 'border-n-weak hover:border-n-strong'
            "
            @click="toggleCategory(cat.category)"
          >
            <span class="block text-xs font-semibold text-n-slate-12">
              {{ cat.category }}
            </span>
            <div class="my-1 flex h-9 items-center justify-center">
              <img
                v-if="cat.imageUrl"
                :src="cat.imageUrl"
                :alt="cat.category"
                class="h-full w-full object-contain"
              />
              <span v-else class="i-lucide-car size-6 text-n-slate-10" />
            </div>
            <span
              v-if="cat.seats"
              class="flex items-center justify-center gap-1 text-[10px] text-n-slate-10"
            >
              <span class="i-lucide-users size-3" />{{ cat.seats }}
            </span>
            <span class="mt-0.5 block text-xs font-semibold text-n-slate-12">
              {{
                t('CONVERSATION.CAR_BOOKING.OFFERS.CATEGORY_FROM', {
                  amount: `${cat.minAmount} ${cat.currency}`,
                })
              }}
            </span>
          </button>
        </div>

        <!-- sort toggle -->
        <div class="mb-4 flex items-center gap-2">
          <span class="text-xs text-n-slate-10">
            {{ t('CONVERSATION.CAR_BOOKING.OFFERS.SORT') }}
          </span>
          <div
            class="inline-flex overflow-hidden rounded-lg border border-n-weak"
          >
            <button
              type="button"
              class="border-r border-n-weak px-3.5 py-1.5 text-sm font-medium transition-colors"
              :class="
                sortDir === 'asc'
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'bg-n-surface-1 text-n-slate-11 hover:text-n-slate-12'
              "
              @click="sortDir = 'asc'"
            >
              {{ t('CONVERSATION.CAR_BOOKING.OFFERS.SORT_PRICE_LOW') }}
            </button>
            <button
              type="button"
              class="px-3.5 py-1.5 text-sm font-medium transition-colors"
              :class="
                sortDir === 'desc'
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'bg-n-surface-1 text-n-slate-11 hover:text-n-slate-12'
              "
              @click="sortDir = 'desc'"
            >
              {{ t('CONVERSATION.CAR_BOOKING.OFFERS.SORT_PRICE_HIGH') }}
            </button>
          </div>
        </div>

        <!-- offers exist but none match filters -->
        <div
          v-if="!filteredOffers.length"
          class="rounded-lg border border-dashed border-n-weak px-4 py-6 text-center text-sm text-n-slate-11"
        >
          <p class="m-0">
            {{ t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_NO_MATCH') }}
          </p>
          <NextButton
            link
            slate
            sm
            class="mt-2"
            :label="t('CONVERSATION.CAR_BOOKING.OFFERS.FILTER_CLEAR')"
            @click="clearFilters"
          />
        </div>

        <!-- offer cards -->
        <ul v-else class="flex flex-col gap-3">
          <li
            v-for="offer in filteredOffers"
            :key="offer.token"
            class="cursor-pointer overflow-hidden rounded-xl border bg-n-surface-1 transition-colors"
            :class="
              isSelected(offer.token)
                ? 'border-n-brand shadow-[0_0_0_1px] shadow-n-brand'
                : 'border-n-weak hover:border-n-strong'
            "
            @click="toggle(offer.token)"
          >
            <div class="flex gap-3.5 p-3.5">
              <!-- checkbox -->
              <span
                class="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors"
                :class="
                  isSelected(offer.token)
                    ? 'border-n-brand bg-n-brand'
                    : 'border-n-strong'
                "
              >
                <span
                  v-if="isSelected(offer.token)"
                  class="i-lucide-check size-3.5 text-white"
                />
              </span>

              <!-- car image -->
              <div
                class="flex h-20 w-32 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2"
              >
                <img
                  v-if="offer.vehicle?.imageUrl"
                  :src="offer.vehicle.imageUrl"
                  :alt="offer.vehicle.sampleModel || offer.category"
                  class="h-full w-full object-contain"
                />
                <span v-else class="i-lucide-car size-8 text-n-slate-10" />
              </div>

              <!-- middle block -->
              <div class="min-w-0 flex-1">
                <p class="m-0 text-sm font-semibold text-n-slate-12">
                  {{ offer.category }}
                </p>
                <p class="m-0 mt-0.5 text-sm text-n-slate-11">
                  {{ offer.vehicle?.sampleModel || offer.sampleModel }}
                  <span class="text-n-slate-10">
                    {{ t('CONVERSATION.CAR_BOOKING.OFFERS.OR_SIMILAR') }}
                  </span>
                </p>
                <div
                  class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-11"
                >
                  <span
                    v-if="offer.vehicle?.seats"
                    class="inline-flex items-center gap-1"
                  >
                    <span class="i-lucide-users size-3.5" />{{
                      offer.vehicle.seats
                    }}
                  </span>
                  <span
                    v-if="offer.vehicle?.bags"
                    class="inline-flex items-center gap-1"
                  >
                    <span class="i-lucide-briefcase size-3.5" />{{
                      offer.vehicle.bags
                    }}
                  </span>
                  <span
                    v-if="offer.vehicle?.transmission"
                    class="inline-flex items-center gap-1"
                  >
                    <span class="i-lucide-settings-2 size-3.5" />
                    <span class="capitalize">{{
                      offer.vehicle.transmission.toLowerCase()
                    }}</span>
                  </span>
                  <span
                    v-if="offer.vehicle?.airCon"
                    class="inline-flex items-center gap-1"
                  >
                    <span class="i-lucide-snowflake size-3.5" />{{
                      t('CONVERSATION.CAR_BOOKING.OFFERS.AC')
                    }}
                  </span>
                  <span
                    v-if="offer.unlimitedKm"
                    class="inline-flex items-center gap-1 text-n-green-11"
                  >
                    <span class="i-lucide-infinity size-3.5" />{{
                      t('CONVERSATION.CAR_BOOKING.OFFERS.UNLIMITED_KM_CHIP')
                    }}
                  </span>
                </div>
              </div>

              <!-- price column -->
              <div
                class="flex w-40 shrink-0 flex-col items-end border-l border-n-weak pl-3 text-right"
              >
                <span
                  v-if="offer.status === 'FREE_SELL'"
                  class="mb-auto text-xs font-semibold text-n-green-11"
                >
                  {{ t('CONVERSATION.CAR_BOOKING.OFFERS.AVAILABLE') }}
                </span>
                <span class="text-2xl font-bold text-n-slate-12">
                  {{ offer.amount }} {{ offer.currency }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{ t('CONVERSATION.CAR_BOOKING.OFFERS.TOTAL') }}
                </span>
                <span
                  v-if="perDayLabel(offer)"
                  class="mt-0.5 text-xs text-n-slate-11"
                >
                  {{ perDayLabel(offer) }}
                </span>
                <span
                  v-if="rentingEquiv(offer)"
                  class="mt-0.5 text-xs text-n-slate-10"
                >
                  {{ rentingEquiv(offer) }}
                  {{ t('CONVERSATION.CAR_BOOKING.OFFERS.AT_PICKUP') }}
                </span>
              </div>
            </div>

            <!-- footer -->
            <div
              class="flex items-center justify-between border-t border-n-weak px-3.5 py-2.5"
            >
              <span class="text-xs text-n-slate-11">
                {{ t('CONVERSATION.CAR_BOOKING.OFFERS.PARTNER') }}
                <span class="font-semibold text-n-slate-12">{{
                  offer.provider
                }}</span>
                <span v-if="offer.ratePlan" class="ml-1 text-n-slate-10">
                  · {{ offer.ratePlan }}
                </span>
              </span>
              <button
                type="button"
                class="mr-auto ml-3 inline-flex items-center gap-1.5 text-xs font-medium transition-colors"
                :class="
                  isCompared(offer)
                    ? 'text-n-brand'
                    : 'text-n-slate-11 hover:text-n-slate-12'
                "
                @click.stop="toggleCompare(offer)"
              >
                <span
                  class="size-3.5"
                  :class="
                    isCompared(offer)
                      ? 'i-lucide-check-square'
                      : 'i-lucide-square'
                  "
                />
                {{ t('CONVERSATION.CAR_BOOKING.COMPARE.ADD') }}
              </button>
              <NextButton
                link
                slate
                sm
                :label="t('CONVERSATION.CAR_BOOKING.OFFERS.DETAILS')"
                @click.stop="carBooking.openDetail(conversationId, offer.token)"
              />
            </div>
          </li>
        </ul>
      </section>
    </div>

    <!-- sticky action bar -->
    <div
      class="flex items-center gap-3 border-t border-n-weak bg-n-surface-2 px-4 py-3"
    >
      <NextButton
        faded
        slate
        :label="t('CONVERSATION.CAR_BOOKING.OFFERS.BACK')"
        @click="back"
      />
      <span v-if="offers.length" class="text-sm text-n-slate-11">
        {{
          t('CONVERSATION.CAR_BOOKING.OFFERS.SELECTED_COUNT', {
            count: selected.length,
          })
        }}
      </span>
      <NextButton
        v-if="compareCount"
        faded
        slate
        class="ml-auto"
        icon="i-lucide-scale"
        :label="
          t('CONVERSATION.CAR_BOOKING.COMPARE.OPEN', { count: compareCount })
        "
        @click="openCompare"
      />
      <NextButton
        v-if="offers.length"
        solid
        blue
        :class="compareCount ? '' : 'ml-auto'"
        :label="shareLabel"
        :disabled="!selected.length || uiFlags.sharing"
        :is-loading="uiFlags.sharing"
        @click="share"
      />
    </div>
  </div>
</template>
